import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const fetchingRef = useRef(false);

  const fetchProfile = useCallback(async (accessToken) => {
    if (fetchingRef.current) return null;
    fetchingRef.current = true;
    try {
      const res = await api.get('/auth/profile', { headers: { Authorization: `Bearer ${accessToken}` } });
      setUser(res.data);
      // Do NOT force the LocationPrompt on login. Address is collected at checkout.
      return res.data;
    } catch {
      setUser(null);
      return null;
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      if (s?.access_token) {
        fetchProfile(s.access_token).finally(() => mounted && setLoading(false));
      } else {
        setLoading(false);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      setSession(s);
      if (s?.access_token) { fetchProfile(s.access_token); }
      else { setUser(null); }
    });
    return () => { mounted = false; subscription.unsubscribe(); };
  }, [fetchProfile]);

  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin } });
    if (error) throw error;
  };

  const loginWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.session) { return { user: await fetchProfile(data.session.access_token) }; }
  };

  const register = async (name, email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: name } } });
    if (error) throw error;
    if (data.session) { return { user: await fetchProfile(data.session.access_token) }; }
    return { message: 'Check your email for confirmation' };
  };

  const logout = async () => { await supabase.auth.signOut(); setUser(null); setSession(null); };
  const getToken = () => session?.access_token || null;

  const updateDeliveryAddress = async (address, phone) => {
    const token = getToken();
    if (!token) return;
    await api.post('/auth/update-address', { delivery_address: address, phone }, { headers: { Authorization: `Bearer ${token}` } });
    setUser(prev => prev ? { ...prev, delivery_address: address, phone } : prev);
    setShowLocationPrompt(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, session, loading, getToken, loginWithGoogle, loginWithEmail, register, logout, showLocationPrompt, setShowLocationPrompt, updateDeliveryAddress }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
